import { FunctionComponent, useId } from "react";
import { FormControl, FormControlProps, FormLabel, FormLabelProps } from "@chakra-ui/react";
import { Box, BoxProps, Flex, Switch, SwitchProps, Text } from "@/ui";
import Arrow from "assets/icons/arrow_down_slim.svg?react";
import Icon from "./Icon";
import { OptionsMenuProps, OptionsMenu, OptionsMenuButton } from "./Menu";

export default function ListField({
  label,
  additionalText,
  icon,
  iconFill,
  props,
  labelProps,
  disabled,
  switch: switchOptions,
  menuOptions,
  children,
  childrenProps,
  childrenOutside
}: {
  label: string;
  additionalText?: string;
  icon?: FunctionComponent;
  iconFill?: string;
  props?: FormControlProps;
  labelProps?: FormLabelProps;
  disabled?: boolean;
  switch?: SwitchProps;
  menuOptions?: Omit<OptionsMenuProps, "children"> & { label: string };
  children?: any;
  childrenProps?: BoxProps;
  childrenOutside?: boolean;
}) {
  const id = useId();
  const longestOption = menuOptions?.items.reduce((all, one) => {
    return all.length > one.value.length ? all : one.value;
  }, "");

  return (
    <FormControl isDisabled={disabled} {...props}>
      <FormLabel
        variant={menuOptions ? "static" : "interactive"}
        border="1px solid"
        borderColor="outline"
        py="12"
        pl="12"
        pr="8"
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        borderRadius="16px"
        htmlFor={id}
        {...labelProps}
      >
        <Flex align="center" columnGap="12px" mr="20" flexGrow={1}>
          {icon && <Icon icon={icon} fill={iconFill} />}
          <Box flexGrow={1}>
            <Text variant="title" color="on-surface">
              {label}
            </Text>
            {additionalText && (
              <Text variant="body" maxW="400px" mt="8">
                {additionalText}
              </Text>
            )}
            {children && !childrenOutside && (
              <Box my="12" {...childrenProps}>
                {children}
              </Box>
            )}
          </Box>
        </Flex>

        <Flex align="center">
          {switchOptions && <Switch id={id} {...switchOptions} />}
          {menuOptions && (
            <Box pos="relative">
              <OptionsMenuButton props={{ visibility: "hidden" }}>
                {longestOption}
                <Icon icon={Arrow} fill="on-surface" />
              </OptionsMenuButton>
              <Box pos="absolute" top="50%" transform="translateY(-50%)" right="0" zIndex={1}>
                <OptionsMenu {...menuOptions}>
                  <OptionsMenuButton>
                    {menuOptions.label}
                    <Icon icon={Arrow} fill="on-surface" />
                  </OptionsMenuButton>
                </OptionsMenu>
              </Box>
            </Box>
          )}
        </Flex>

        {children && childrenOutside && (
          <Box flexBasis="100%" {...childrenProps}>
            {children}
          </Box>
        )}
      </FormLabel>
    </FormControl>
  );
}
